import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Option } from '@saucelabs/ds-inputs';
import { Typography } from '@saucelabs/ds-typography';
import { constants as appStorageConstants } from '@saucelabs/sl-module-app-storage';

import { selectors as groupsSelectors } from '../../group';
import selectors from '../selectors';
import operations from '../operations';
import { ValueOf } from '../../../GlobalTypes';

type OrientationValue =
  | ValueOf<typeof appStorageConstants.APP_STORAGE_SETTINGS.ORIENTATION>
  | typeof DEFAULT_VALUE;

const DEFAULT_VALUE = 'DEFAULT';

const Orientation: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const settings = useSelector(groupsSelectors.currentGroupSettingsSelector);
  const currentGroup = useSelector(groupsSelectors.currentGroupSelector);
  const isLoading = useSelector(selectors.isSettingsLoadingSelector);

  const selectedValue: OrientationValue = settings?.orientation ?? DEFAULT_VALUE;

  const selectionHandler = useCallback((newValue: OrientationValue) => {
    if (currentGroup) {
      void dispatch(operations.updateSettings({
        groupId: currentGroup.id,
        originalValues: {
          orientation: selectedValue === DEFAULT_VALUE ? null : selectedValue,
        },
        settingsName: 'device orientation',
        updatedValues: {
          orientation: newValue === DEFAULT_VALUE ? null : newValue, // null will reset any other set values in the backend
        },
      }));
    }
  }, [currentGroup, dispatch, selectedValue]);

  return (
    <div className="flex flex-row items-baseline justify-between">
      <Typography className="min-w-16 text-high-emphasis font-semibold">
        Device orientation
      </Typography>

      <div className="overflow-auto">
        <Select
          aria-label="Device orientation"
          data-qa="app-management-settings-orientation"
          isDisabled={isLoading}
          onSelectionChange={selectionHandler}
          selectedKey={selectedValue}
        >
          <Option key={DEFAULT_VALUE}>
            Default Orientation
          </Option>

          <Option key={appStorageConstants.APP_STORAGE_SETTINGS.ORIENTATION.LANDSCAPE}>
            Landscape
          </Option>

          <Option key={appStorageConstants.APP_STORAGE_SETTINGS.ORIENTATION.PORTRAIT}>
            Portrait
          </Option>
        </Select>
      </div>
    </div>
  );
};

export default Orientation;
